import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  setFocusBlock,
  setHoverBlock,
  updateBlocksList,
} from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import { Block, mapBlocksListToIds } from 'src/config/api/models/Block';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { DraggingType } from '../types';
import { PageBlocksWrapperStyled, RowWrapper } from '../Atoms';
import { moveBlock } from '../helper';
import { reorderBlocks } from 'src/modules/shared/slices/blocks/api/blocks';
import { lazyWithRetry } from 'src/modules/shared/helpers/lazyRetry';

const ClusterRow = lazyWithRetry(() => import('./Cluster/ClusterRow'));
const BlockRow = lazyWithRetry(() => import('./Block/BlockRow'));
const DropRow = lazyWithRetry(() => import('./DropRow'));
const RowButtonGroup = lazyWithRetry(() => import('./RowButtonGroup'));

type Props = {
  bookId: string;
  pageId: string;
  pageBlocks: Block[];
};

const DraggingInitialState: DraggingType = {
  isDragging: false,
  id: '',
  index: 0,
  type: 'pageBlock',
  clusterId: '',
  sectionId: '',
};

const PageBlocks: FC<Props> = ({ bookId, pageId, pageBlocks }) => {
  // hooks
  const dispatch = useAppDispatch();
  // redux
  const focusBlock = useAppSelector((state) => state.blocks.focusBlock);
  //state
  const [dragging, setDragging] = useState<DraggingType>(DraggingInitialState);
  const [hoveredDropBlockId, setHoveredDropBlockId] = useState('');

  const handleClickBlockLink = useCallback(
    (blockId: string) => {
      setTimeout(() => {
        const indicator = document.querySelector(`#block_hover_scroll_target_${blockId}`);
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });

        if (focusBlock === blockId) {
          dispatch(setFocusBlock(undefined));
          dispatch(setHoverBlock(undefined));
        } else {
          dispatch(setFocusBlock(blockId));
          dispatch(setHoverBlock(blockId));
        }
      }, 100);
    },
    [focusBlock, dispatch]
  );

  const handleUpdateBlocksList = useCallback(
    (newBlockList: Block[]) => {
      dispatch(updateBlocksList(newBlockList));
      dispatch(reorderBlocks({ bookId, pageId, newBlockList: mapBlocksListToIds(newBlockList) }));
    },
    [dispatch, bookId, pageId]
  );

  /**
   * Handling the move/drop of a block
   * @param source - block that is being moved
   * @param destination - other block's dropzone that received the moved block
   */
  const handleDropBlock = useCallback(
    (
      source: {
        type: 'cluster' | 'clusterBlock' | 'pageBlock';
        clusterId: string;
        sectionId: string;
        index: number;
      },
      destination: {
        type: 'cluster' | 'clusterBlock' | 'pageBlock';
        clusterId: string;
        sectionId: string;
        index: number;
      }
    ) => {
      const newBlockList = moveBlock(source, destination, pageId, pageBlocks);
      if (!!newBlockList) {
        handleUpdateBlocksList(newBlockList);
      }
    },
    [handleUpdateBlocksList, pageBlocks, pageId]
  );

  const handleSetHoveredDropBlockId = (blockId: string) => {
    setHoveredDropBlockId(blockId);
  };

  /**
   * As some block is dragging unselect the hoveredBlock and the focusBlock in case
   */
  useEffect(() => {
    if (dragging.isDragging) {
      if (focusBlock && dragging.id !== focusBlock) {
        dispatch(setFocusBlock(undefined));
        dispatch(setHoverBlock(undefined));
      }
    }
  }, [dragging, dispatch, focusBlock]);

  useEffect(() => {
    if (pageBlocks?.find((block) => block.id === focusBlock)?.id) {
      setTimeout(() => {
        const indicator = document.querySelector(
          `#focus-block-${pageBlocks?.find((block) => block.id === focusBlock)?.id}`
        );
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [focusBlock, pageBlocks]);

  // initialize cluster index
  let clusterIndex = 1;

  const showPageTopDropRow = useMemo(() => {
    return dragging.isDragging && (dragging.type !== 'pageBlock' || dragging.index > 0);
  }, [dragging.index, dragging.isDragging, dragging.type]);

  const showDropRowAfterBlock = useCallback(
    (index: number) =>
      dragging.isDragging &&
      (dragging.type !== 'pageBlock' ||
        (dragging.index !== pageBlocks.length &&
          dragging.index !== index &&
          dragging.index !== index + 1)),
    [dragging.index, dragging.isDragging, dragging.type, pageBlocks.length]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <PageBlocksWrapperStyled>
        {showPageTopDropRow && (
          <RowWrapper onDragEnter={() => handleSetHoveredDropBlockId('topPage')}>
            <DropRow
              previousBlockId={pageId}
              previousBlockIndex={-1}
              onDropItem={handleDropBlock}
              showMaximized={hoveredDropBlockId === 'topPage'}
              type="pageBlock"
            />
          </RowWrapper>
        )}
        {pageBlocks.map((block: Block, index: number) => (
          <RowWrapper
            key={block.id}
            className={`${block.type === 'cluster' && 'is-cluster'}`}
            onDragEnter={() => handleSetHoveredDropBlockId(block.id)}
          >
            {!dragging.isDragging && index === 0 && (
              <RowButtonGroup
                showOnHover
                pageId={pageId}
                insideCluster={false}
                previousBlockId={''}
              />
            )}
            {block.type !== 'cluster' && (
              <BlockRow
                pageId={pageId}
                block={block}
                bookId={bookId}
                index={index}
                type={'pageBlock'}
                onDragging={setDragging}
                dragging={dragging}
                focusBlock={focusBlock}
                onClickLink={handleClickBlockLink}
              />
            )}
            {block.type === 'cluster' && (
              <ClusterRow
                pageId={pageId}
                bookId={bookId}
                index={index}
                clusterIndex={clusterIndex++}
                onDragging={setDragging}
                cluster={block}
                dragging={dragging}
                onDropItem={handleDropBlock}
                focusBlock={focusBlock}
                onClickLink={handleClickBlockLink}
                isDragOver={hoveredDropBlockId === block.id}
                onSetHoveredDropBlockId={handleSetHoveredDropBlockId}
                hoveredDropBlockId={hoveredDropBlockId}
              />
            )}
            {showDropRowAfterBlock(index) && (
              <DropRow
                previousBlockId={block.id}
                previousBlockIndex={index}
                onDropItem={handleDropBlock}
                clusterId={(block.type === 'cluster' && block.id) || ''}
                type={'pageBlock'}
                showMaximized={hoveredDropBlockId === block.id}
              />
            )}
          </RowWrapper>
        ))}
      </PageBlocksWrapperStyled>
    </DndProvider>
  );
};

export default PageBlocks;
